import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Pet } from '../interfaces/pet.interfaces';
import { environment } from '../../environments/environment';

interface BackendPet {
  _id: string;
  orden?: number;
  timestamp_registry?: string | Date;
  email?: string;
  responsibleOwner?: string;
  address?: string;
  phoneNumbers?: string;
  petName?: string;
  petType?: string;
  petSex?: string;
  petRace?: string;
  petAge?: string;
  lastVaccineDate?: string | Date;
  lastDewormingDate?: string | Date;
  lastPeriod?: string | Date;
  lastLactancy?: string | Date;
  alergies?: string;
  hasIdentifier?: boolean;
  medicalIndications?: boolean;
  status?: string;
  folio?: string;
  appointmentDate?: string | Date;
}

interface ApiListResponse {
  status: string;
  results: number;
  data: { documents: BackendPet[] };
}

interface ApiDocumentResponse {
  status: string;
  data: { document: BackendPet };
}

function toDate(value: string | Date | undefined): Date {
  if (!value) return new Date();
  return value instanceof Date ? value : new Date(value);
}

function mapPet(doc: BackendPet): Pet {
  return {
    id: doc._id,
    orden: doc.orden ?? 0,
    timestamp_registry: toDate(doc.timestamp_registry),
    email: doc.email ?? '',
    responsibleOwner: doc.responsibleOwner ?? '',
    address: doc.address ?? '',
    phoneNumbers: doc.phoneNumbers ?? '',
    petName: doc.petName ?? '',
    petType: doc.petType ?? '',
    petSex: doc.petSex ?? '',
    petRace: doc.petRace ?? '',
    petAge: doc.petAge ?? '',
    lastVaccineDate: toDate(doc.lastVaccineDate),
    lastDewormingDate: toDate(doc.lastDewormingDate),
    lastPeriod: toDate(doc.lastPeriod),
    lastLactancy: toDate(doc.lastLactancy),
    alergies: doc.alergies ?? '',
    hasIdentifier: doc.hasIdentifier ?? false,
    medicalIndications: doc.medicalIndications ?? false,
    status: doc.status ?? '',
    folio: doc.folio ?? '',
    appointmentDate: toDate(doc.appointmentDate),
  };
}

@Injectable({
  providedIn: 'root',
})
export class PetService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/pet`;

  newPet(pet: Partial<Pet>): Promise<Pet> {
    return this.http
      .post<ApiDocumentResponse>(`${this.baseUrl}/`, pet)
      .pipe(map((res) => mapPet(res.data.document)))
      .toPromise()
      .then((r) => r as Pet);
  }

  getAllPets(): Observable<Pet[]> {
    return this.http.get<ApiListResponse>(`${this.baseUrl}/`).pipe(
      map((res) => res.data.documents.map(mapPet)),
    );
  }

  async getPetById(id: string): Promise<Pet> {
    const res = await this.http
      .get<ApiDocumentResponse>(`${this.baseUrl}/${id}`)
      .toPromise();
    return mapPet(res!.data.document);
  }

  updatePet(id: string, pet: Pet): void {
    this.http.patch(`${this.baseUrl}/${id}`, pet).subscribe();
  }
}
